import React, { useCallback, useMemo, useRef } from 'react';

import { IFilterModel } from '@common/types';
import { IEnhancedRow } from '@components/table/types';
import ListingTableWithCellInputs from '@components/table/with-cell-inputs/ListingTableWithCellInputs';

import { getAmendmentEndorsementCertificatesHeaders } from '../constants/amendmentEndorsementCertificatesHeader.constant';
import {
  FormValues,
  IAmendmentEndorsementInsuredDetails,
  IInsuredsDetails
} from '../types';
import { getInsuredPolicyMedicalRowFieldErrors } from '../utils/getInsuredPolicyMedicalRowFieldErrors';
import { parseVarianceInputValue } from '../utils/parseVarianceInputValue';
import { recalculateInsuredPolicyMedicalVariances } from '../utils/recalculateInsuredPolicyMedicalVariances';

import { useFormikContext } from 'formik';

type VarianceField = 'netPremiumVariance' | 'chargesVariance' | 'tpaFeesVariance';

type InitialVariance = Record<string, Record<VarianceField, number>>;

type CertificateDisplayRow = (
  | IInsuredsDetails
  | IAmendmentEndorsementInsuredDetails
) & {
  netPremiumVarianceMinValue: number;
  chargesVarianceMinValue: number;
  tpaFeesVarianceMinValue: number;
};

const toFormRow = (row: CertificateDisplayRow): IInsuredsDetails => {
  const {
    netPremiumVarianceMinValue: _netMin,
    chargesVarianceMinValue: _chargesMin,
    tpaFeesVarianceMinValue: _tpaMin,
    ...formRow
  } = row;

  return formRow as IInsuredsDetails;
};

type Props = {
  filterValues: IFilterModel;
  disabled: boolean;
  tableData: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    pagedItems: IInsuredsDetails[] | IAmendmentEndorsementInsuredDetails[];
  };
  layoutName: 'amendmentEndorsementCertificatesDetails';
  handlePageChange: (filter: IFilterModel) => Promise<void>;
  policyCurrency: string;
};

const VARIANCE_FIELDS: VarianceField[] = [
  'netPremiumVariance',
  'chargesVariance',
  'tpaFeesVariance'
];

const VALUES_CHANGED_MESSAGE = 'Values have been changed.';

const EndorsementCertificatesDetailsWidgetAtom: React.FC<Props> = ({
  filterValues,
  disabled,
  handlePageChange,
  policyCurrency,
  tableData
}) => {
  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const initialVarianceRef = useRef<InitialVariance>({});
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const captureInitialVariances = useCallback(
    (items: (IInsuredsDetails | IAmendmentEndorsementInsuredDetails)[]) => {
      const next: InitialVariance = {};
      items.forEach((item) => {
        next[item.id] = {
          netPremiumVariance: Number(item.netPremiumVariance ?? 0),
          chargesVariance: Number(item.chargesVariance ?? 0),
          tpaFeesVariance: Number(item.tpaFeesVariance ?? 0)
        };
      });
      initialVarianceRef.current = next;
    },
    []
  );

  React.useEffect(() => {
    captureInitialVariances(tableData.pagedItems);
  }, [captureInitialVariances, tableData.pagedItems]);

  const dynamicRowMessage = (row: IEnhancedRow): string[] => {
    const initial = initialVarianceRef.current[row.columns.id];
    if (!initial) {
      return [];
    }

    for (const field of VARIANCE_FIELDS) {
      if (Number(row.columns[field]) !== Number(initial[field])) {
        return [VALUES_CHANGED_MESSAGE];
      }
    }

    return [];
  };

  const mergeCertificateRow = useCallback(
    (
      tableRow: IInsuredsDetails | IAmendmentEndorsementInsuredDetails,
      index: number,
      overrides: Partial<IInsuredsDetails> = {}
    ): CertificateDisplayRow => {
      const endorsementRow = tableRow as IAmendmentEndorsementInsuredDetails;
      const formRow = values.insuredPolicyMedicals?.[index];
      const merged = {
        ...tableRow,
        ...(formRow ?? {}),
        ...overrides
      };

      return {
        ...merged,
        certificateNumber:
          merged.certificateNumber ??
          endorsementRow.policyNumber ??
          merged.id,
        certificateID: merged.certificateID ?? merged.id,
        netPremiumVarianceMinValue: -Number(merged.netPremium ?? 0),
        chargesVarianceMinValue: -Number(merged.charges ?? 0),
        tpaFeesVarianceMinValue: -Number(merged.tpaFees ?? 0)
      };
    },
    [values.insuredPolicyMedicals]
  );

  const handleTableValueChange = (
    index: number,
    columnName: string,
    value: unknown
  ) => {
    const tableRow = tableData.pagedItems[index];
    if (!tableRow) {
      return;
    }

    const parsedValue = parseVarianceInputValue(value);
    if (parsedValue === null) {
      return;
    }

    void setFieldValue(
      `insuredPolicyMedicals[${index}]`,
      toFormRow(
        mergeCertificateRow(tableRow, index, {
          [columnName]: parsedValue
        })
      ),
      true
    );
  };

  const validateTableVariances = (
    index: number,
    columnName: string,
    value: unknown,
    row: IInsuredsDetails
  ) => {
    const parsedValue = parseVarianceInputValue(value, { finalize: true });
    if (parsedValue === null) {
      return;
    }

    const tableRow = tableData.pagedItems[index] ?? row;
    const mergedRow = mergeCertificateRow(tableRow, index, {
      ...(columnName ? { [columnName]: parsedValue } : {})
    });
    const recalculated = recalculateInsuredPolicyMedicalVariances(mergedRow);

    void setFieldValue(
      `insuredPolicyMedicals[${index}]`,
      toFormRow({
        ...mergedRow,
        ...recalculated
      }),
      true
    );
  };

  const displayPagedItems = useMemo(
    () =>
      tableData.pagedItems.map((item, index) => ({
        ...mergeCertificateRow(item, index),
        fieldValidationErrors: getInsuredPolicyMedicalRowFieldErrors(
          errors,
          index
        )
      })),
    [errors, mergeCertificateRow, tableData.pagedItems]
  );

  return (
    <ListingTableWithCellInputs
      name="endorsementCertificatesDetails"
      inlineTitle="Certificates Details"
      headers={getAmendmentEndorsementCertificatesHeaders(
        policyCurrency,
        disabled
      )}
      data={{
        pageNumber: tableData.pageNumber,
        pageSize: tableData.pageSize,
        totalCount: tableData.totalCount,
        pagedItems: displayPagedItems
      }}
      handleCellValueChanged={(index, columnName, value) => {
        handleTableValueChange(index, columnName, value);
      }}
      handleUpdate={(index, columnName, value, row) => {
        if (!row) {
          return;
        }
        validateTableVariances(
          index,
          columnName,
          value,
          row as IInsuredsDetails
        );
      }}
      inline
      scrollable
      dynamicRowMessage={dynamicRowMessage}
      disableSelection
      defaultOrderByColumn="certificateNumber"
      orderByAscendingByDefault
      onPageChange={(page, pageSize) => {
        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: page,
            pageSize
          },
          orderBy: filterValues.orderBy,
          descending: filterValues.descending,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
      onSortChange={(order, orderBy) => {
        const incomingOrderBy = order?.trim();

        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: 1,
            pageSize: filterValues.pagination.pageSize
          },
          orderBy: incomingOrderBy || filterValues.orderBy,
          descending: orderBy,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
      onSearchChange={(searchKey) => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }

        searchDebounceRef.current = setTimeout(() => {
          const newFilterModel: IFilterModel = {
            ...filterValues,
            pagination: {
              pageNumber: 1,
              pageSize: filterValues.pagination.pageSize
            },
            orderBy: filterValues.orderBy,
            descending: filterValues.descending,
            searchKey
          };

          void handlePageChange(newFilterModel);
        }, 300);
      }}
    />
  );
};

export { EndorsementCertificatesDetailsWidgetAtom };
